import React from "react";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { ControllerRenderProps } from "react-hook-form";
import { dt } from "../../../themes/redesignTokens";

export interface ChipOption<T extends string | number = number> {
  code: T;
  label: string;
  icon?: React.ReactNode;
  color: string;
  dotOnly?: boolean;
}

export interface FormChipSelectorProps<T extends string | number = number> {
  options: ChipOption<T>[];
  value: T;
  onChange: (value: T) => void;
  // react-hook-form mode
  field?: ControllerRenderProps<Record<string, unknown>, string>;
}

/**
 * Selectable chip group for mutually exclusive options.
 *
 * Based on WO type/priority chip pattern.
 * Supports icons (type picker) and dot indicators (priority picker) via dotOnly flag.
 *
 * Works with both react-hook-form Controller and standalone onChange.
 */
function FormChipSelector<T extends string | number = number>({
  options,
  value,
  onChange,
  field,
}: FormChipSelectorProps<T>) {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));

  const resolvedValue = field ? (field.value as T) : value;

  const handleSelect = (code: T) => {
    if (field) {
      field.onChange(code);
    }
    onChange(code);
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: "10px",
        flexWrap: "wrap",
      }}
    >
      {options.map((option) => {
        const selected = resolvedValue === option.code;

        return (
          <Box
            key={String(option.code)}
            onClick={() => handleSelect(option.code)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "10px 16px",
              borderRadius: dt.radiusSm,
              cursor: "pointer",
              border: selected
                ? `2px solid ${option.color}`
                : `1px solid ${dt.cardBorder}`,
              backgroundColor: selected ? `${option.color}08` : dt.cardBg,
              transition: "all 0.2s ease",
              "&:hover": {
                borderColor: option.color,
                backgroundColor: `${option.color}06`,
              },
            }}
          >
            {option.dotOnly ? (
              <Box
                sx={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor: option.color,
                }}
              />
            ) : option.icon ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  "& .MuiSvgIcon-root": {
                    fontSize: 18,
                    color: option.color,
                  },
                }}
              >
                {option.icon}
              </Box>
            ) : null}
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "13px",
                fontWeight: selected ? 600 : 400,
                color: selected ? option.color : dt.textPrimary,
              }}
            >
              {option.label}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

export default FormChipSelector;
